import { useMemo } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useField } from 'formik';
import { TransactionPricingOfferPayload } from 'types/graphql';

import { Flex } from 'src/components/Flex';
import MainCard from 'src/components/MainCard/MainCard';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useTransactionChargeContext } from 'src/contexts/TransactionChargeProvider';
import { useTransactionPricing } from 'src/hooks';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';

interface TransactionPricingProps {
    handleOpenTransactionCharges: () => void;
    handleEditTransactionCharges: () => void;
}

function TransactionPricing({
    handleOpenTransactionCharges,
    handleEditTransactionCharges,
}: TransactionPricingProps) {
    const { activeTransactionPricingOffers, TRANSACTION_PRICING_OFFER_ID_KEY } =
        useTransactionPricing();
    const { _, selectedActiveTransactionPricingOffer } = useTransactionChargeContext();
    const [{ value: transactionPricingOfferId }] = useField(
        `service.${TRANSACTION_PRICING_OFFER_ID_KEY}`
    );

    const { merchantApplication } = useAddMerchantContext();

    const transactionPricingOffer: TransactionPricingOfferPayload = useMemo(() => {
        if (
            selectedActiveTransactionPricingOffer &&
            selectedActiveTransactionPricingOffer?.id?.toString() === transactionPricingOfferId
        ) {
            return selectedActiveTransactionPricingOffer;
        } else if (transactionPricingOfferId) {
            return activeTransactionPricingOffers.find((el) => {
                return el.id === Number(transactionPricingOfferId);
            });
        }
        return null;
    }, [
        selectedActiveTransactionPricingOffer,
        transactionPricingOfferId,
        activeTransactionPricingOffers,
    ]);

    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <Box
            display="flex"
            flexDirection="column"
            sx={{ opacity: transactionPricingOfferId ? 1 : 0.5 }}
        >
            <MainCard>
                <Typography fontSize="24px" color="#575757" marginBottom="8px">
                    Transaction Pricing
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Flex flexDirection="column" gap="8px">
                    <Typography fontWeight={700} color="#575757" fontSize="14px" lineHeight="21px">
                        Service Transaction Pricing Offer
                    </Typography>
                    <Flex alignItems="center">
                        {transactionPricingOffer && (
                            <Typography
                                fontSize="14px"
                                color="#575757"
                                marginBottom="8px"
                                marginRight="164px"
                            >
                                {transactionPricingOffer.offerName}
                            </Typography>
                        )}
                        <Flex gap="16px">
                            <Button
                                startIcon={<RemoveRedEyeIcon />}
                                variant="npi-primary"
                                onClick={handleOpenTransactionCharges}
                                sx={{ width: '128px' }}
                                disabled={!transactionPricingOfferId}
                            >
                                View
                            </Button>

                            <Button
                                startIcon={<EditIcon />}
                                variant="npi-primary"
                                onClick={handleEditTransactionCharges}
                                sx={{ width: '128px' }}
                                disabled={!transactionPricingOfferId || isEditingDisabled}
                            >
                                Edit
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            </MainCard>
        </Box>
    );
}

export default TransactionPricing;
